import { useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, AppBar, Toolbar, IconButton } from '@mui/material';
import Iconify from '../../../components/iconify';
import { HeaderNav } from '../../../components/header-nav';
import { ScopeSelector } from '../../../components/scope-selector';
import { useProject } from '../../../hooks/ProjectContext';

const HEADER_HEIGHT = 64;

const StyledRoot = styled(AppBar, {
  shouldForwardProp: (p) => p !== 'navWidth',
})(({ theme, navWidth }) => ({
  backgroundColor: '#FFFFFF',
  boxShadow: 'none',
  borderBottom: '1px solid #DFE3E8',
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${navWidth + 1}px)`,
  },
}));

const StyledToolbar = styled(Toolbar)({
  minHeight: HEADER_HEIGHT,
  padding: '0 24px',
});

const HIDE_SCOPE_PATTERNS = ['/admin/device/', '/admin/system'];

Header.propTypes = {
  onOpenNav: PropTypes.func,
  onToggleNavCollapse: PropTypes.func,
  isNavCollapsed: PropTypes.bool,
  navWidth: PropTypes.number,
};

export default function Header({ onOpenNav, onToggleNavCollapse, isNavCollapsed, navWidth }) {
  const { pathname } = useLocation();
  const { availableProjects, selectedProject, setSelectedProject, availableSites, selectedSite, setSelectedSite } = useProject();
  const [, setState] = useState(0);

  const showScope = !HIDE_SCOPE_PATTERNS.some(p => pathname.startsWith(p));

  return (
    <StyledRoot navWidth={navWidth}>
      <StyledToolbar>
        <IconButton onClick={onOpenNav} sx={{ mr: 1, color: 'text.primary', display: { lg: 'none' } }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
        <IconButton onClick={onToggleNavCollapse}
          sx={{ mr: 1, color: 'text.primary', display: { xs: 'none', lg: 'inline-flex' } }}
          aria-label={isNavCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <Iconify icon={isNavCollapsed ? 'eva:arrow-ios-forward-fill' : 'eva:arrow-ios-back-fill'} />
        </IconButton>

        {showScope && availableProjects.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <ScopeSelector
              label="All projects"
              value={selectedProject?.name}
              options={[{ id: null, name: 'All projects' }, ...availableProjects]}
              onChange={proj => { setSelectedProject(proj.id ? proj : null); setState(n => n + 1); }}
            />
            {selectedProject && availableSites.length > 0 && (
              <>
                <span style={{ color: '#919EAB', margin: '0 4px', fontSize: 12 }}>/</span>
                <ScopeSelector
                  label="All sites"
                  value={selectedSite?.name}
                  options={[{ id: null, name: 'All sites' }, ...availableSites]}
                  onChange={site => { setSelectedSite(site.id ? site : null); setState(n => n + 1); }}
                />
              </>
            )}
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <HeaderNav settingsPath="/admin/settings" />
      </StyledToolbar>
    </StyledRoot>
  );
}
